import { createEffect, onCleanup, untrack } from 'solid-js';
import { defaultChannelsStore } from '../../../../store/defaultChannelsStore';
import { alarmsExploreMapMarkers } from '../../../../store/alarmsExploreMapMarkersStore';
import {
  getInterpolatedPointAtTimeFromSortedTrack,
  getInterpolatedPointAtTimePerSource,
} from '../../../../utils/trackInterpolation';
import type { AlarmExploreMapMarker } from '../../../../utils/alarmsExploreMapMarkers';
import {
  computeTrackWindow,
  filterMarkersToSelectedSources,
  selectExpiredMarkerIds,
  selectPendingMarkers,
} from '../../../../utils/alarmsExploreMarkerLifecycle';
import { themeStore } from '../../../../store/themeStore';
import { debug } from '../../../../utils/console';

/**
 * Alarm markers, drawn as a Mapbox GL `circle` layer in MAP COORDINATES.
 *
 * An alarm row carries a TIME and no position (see shared/utils/alarmStreams ALARM_COLUMNS), so a
 * marker's lat/lng has to be interpolated from the boat track. The question is when.
 *
 * This used to be a D3 SVG overlay holding screen-space x/y, re-derived from the track on every
 * `move`/`zoom`/`resize`. Three things were wrong with that, and they are the same three things:
 *
 *  - NOT ATTACHED TO THE MAP. Screen coordinates are only valid for one camera position, so every
 *    pan and zoom needed a reproject, and that reproject ran a frame behind Mapbox's own transform.
 *    The markers visibly swam against the track they are supposed to be pinned to. Handing Mapbox a
 *    lng/lat and letting it transform the layer removes the problem rather than chasing it: the
 *    marker is now in the same coordinate space as the track, so they cannot disagree.
 *  - RE-DERIVED CONSTANTLY. Each redraw re-walked the track for every marker. On a live map
 *    `trackData` is the SLIDING WINDOW, so the interpolation input kept changing underneath a
 *    marker whose instant had not moved at all — the position drifted with the window.
 *  - NEVER DELIBERATELY REMOVED. Markers disappeared only when interpolation happened to fail,
 *    which also took out any marker sitting in an ordinary track gap.
 *
 * So: resolve each marker's position ONCE, the first time the track covers its instant, and keep it
 * keyed by the marker's stable content-derived id. Drop it when its instant leaves the window the
 * track covers. Between those two events nothing is recomputed and nothing is projected — Mapbox
 * moves the layer.
 *
 * A `circle` layer, not a `symbol` layer: symbols crash Mapbox's incremental placement while a
 * `line` layer (the GL track) coexists, which is why GlManeuverLayer uses Canvas2D. Circles do not
 * go through the placement engine, so this is safe with MAP_GL_NATIVE on or off, and there is still
 * only one code path.
 *
 * FLEET vs SINGLE. On the day and live maps `trackData` is a mixed-source array, so interpolating a
 * marker against the whole thing would place it on whichever boat happened to be nearest in time.
 * When markers carry a `source`, positions are resolved per source instead.
 */

const SRC_ID = 'alarms-explore-markers-src';
const LYR_ID = 'alarms-explore-markers-lyr';

/** Category colours — kept in step with the `.alarms-explore-map-marker--*` rules and the pills. */
const CATEGORY_COLOR: Record<string, string> = {
  penalty: '#ef4444',
  warning: '#f97316',
  info: '#ffffff',
};

const MARKER_RADIUS_PX = 5;
const MARKER_STROKE_WIDTH = 1.5;

/** Outline contrast flips with the theme, as the `[data-theme="dark"]` rule used to do in CSS. */
const strokeColor = (): string =>
  themeStore.isDark() ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.45)';

/** Minimal Mapbox surface this layer needs. */
export interface AlarmsExploreMarkersMap {
  addSource: (id: string, src: unknown) => void;
  addLayer: (layer: unknown) => void;
  getSource: (id: string) => { setData?: (data: unknown) => void } | undefined;
  getLayer: (id: string) => unknown;
  removeLayer: (id: string) => void;
  removeSource: (id: string) => void;
  setPaintProperty?: (layerId: string, name: string, value: unknown) => void;
  isStyleLoaded?: () => boolean;
  getCanvas?: () => HTMLCanvasElement;
  on: (...args: unknown[]) => void;
  off: (...args: unknown[]) => void;
}

export interface AlarmsExploreMarkersLayerProps {
  map: AlarmsExploreMarkersMap | null | undefined;
  trackData: unknown[];
  enabled: boolean;
  /**
   * Boat names currently selected on the MAP, in any case.
   *
   * The Alarms page and the map have separate selections: the page lists the fleet's alarms, the
   * map draws the boats you asked for. Without this the layer drew a marker for every alarm the
   * page held, including boats absent from the map — and once a marker had been resolved, the
   * position cache kept it on screen even after its boat was deselected, because nothing in the
   * expiry rule mentioned the source.
   *
   * Omit to disable the filter (a single-source page has nothing to filter against).
   */
  selectedSources?: Iterable<string> | null;
}

function getTimestampFromPoint(d: unknown): Date {
  if (d == null || typeof d !== 'object') {return new Date(0);}
  const row = d as Record<string, unknown>;
  const timestamp = row.Datetime;
  if (timestamp == null) {return new Date(0);}
  if (timestamp instanceof Date) {return timestamp;}
  if (typeof timestamp === 'number') {return new Date(timestamp);}
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) {return parsed;}
  }
  return new Date(0);
}

function makeLatLngAccessors() {
  const { latName, lngName } = defaultChannelsStore;
  const pick = (d: unknown, field: string, ...fallbacks: string[]): number | undefined => {
    if (d == null || typeof d !== 'object') {return undefined;}
    const row = d as Record<string, unknown>;
    let val = row[field] ?? row[field.toLowerCase()] ?? row[field.toUpperCase()];
    for (const f of fallbacks) {
      if (val !== undefined && val !== null) {break;}
      val = row[f];
    }
    if (val === undefined || val === null) {return undefined;}
    const numVal = Number(val);
    return Number.isNaN(numVal) ? undefined : numVal;
  };
  return {
    getTimestamp: getTimestampFromPoint,
    getLat: (d: unknown) => pick(d, latName(), 'Lat', 'lat'),
    getLng: (d: unknown) => pick(d, lngName(), 'Lng', 'lng'),
  };
}

/** Boat name for a track row, normalized for comparison with a marker's `source`. */
function trackRowSourceKey(d: unknown): string {
  if (d == null || typeof d !== 'object') {return '';}
  const row = d as Record<string, unknown>;
  const raw = row.SOURCE_NAME ?? row.source_name ?? row.Source ?? row.source;
  return String(raw ?? '').trim().toUpperCase();
}

/** A marker whose position has been resolved and no longer needs the track. */
interface ResolvedMarker {
  lng: number;
  lat: number;
  atMs: number;
  category: string;
  tooltip: string;
}

export default function AlarmsExploreMarkersLayer(props: AlarmsExploreMarkersLayerProps) {
  /**
   * Resolved positions, by the marker's stable content-derived id.
   *
   * Survives every redraw, which is the whole point — an alarm's position is a property of its
   * instant, not of the camera or of whatever slice of track happens to be loaded. The ids are
   * derived from content (see buildAlarmExploreMapMarkers), so a poll that rebuilds the marker
   * array with the same alarms in it hits this cache rather than re-interpolating.
   */
  const resolved = new Map<string, ResolvedMarker>();

  const styleReady = (map: AlarmsExploreMarkersMap): boolean => {
    try {
      return typeof map.isStyleLoaded !== 'function' || map.isStyleLoaded();
    } catch {
      return false;
    }
  };

  const emptyFc = () => ({ type: 'FeatureCollection', features: [] });

  const ensureLayer = (map: AlarmsExploreMarkersMap): boolean => {
    try {
      if (map.getLayer(LYR_ID)) {return true;}
      if (!map.getSource(SRC_ID)) {
        map.addSource(SRC_ID, { type: 'geojson', data: emptyFc() });
      }
      map.addLayer({
        id: LYR_ID,
        type: 'circle',
        source: SRC_ID,
        paint: {
          'circle-radius': MARKER_RADIUS_PX,
          // Data-driven from the feature's own category, so one layer serves all three and a
          // category change is a setData, not a layer rebuild.
          'circle-color': [
            'match',
            ['get', 'category'],
            'penalty', CATEGORY_COLOR.penalty,
            'warning', CATEGORY_COLOR.warning,
            'info', CATEGORY_COLOR.info,
            CATEGORY_COLOR.info,
          ],
          'circle-stroke-color': strokeColor(),
          'circle-stroke-width': MARKER_STROKE_WIDTH,
        },
      });
      return true;
    } catch (err) {
      debug('[AlarmsExploreMarkersLayer] addSource/addLayer failed', err);
      return false;
    }
  };

  const removeLayer = (map: AlarmsExploreMarkersMap) => {
    try {
      if (map.getLayer(LYR_ID)) {map.removeLayer(LYR_ID);}
      if (map.getSource(SRC_ID)) {map.removeSource(SRC_ID);}
    } catch {
      /* map may already be torn down */
    }
  };

  const setData = (map: AlarmsExploreMarkersMap, features: unknown[]) => {
    try {
      const src = map.getSource(SRC_ID);
      src?.setData?.({ type: 'FeatureCollection', features });
    } catch (err) {
      debug('[AlarmsExploreMarkersLayer] setData failed', err);
    }
  };

  /**
   * Reconcile the cache against the markers and the track window, then publish.
   *
   * Deliberately NOT wired to `move`/`zoom`: none of this depends on the camera. It runs when the
   * alarms change or the track window moves, which is the only time the answer can differ.
   */
  const sync = () => {
    untrack(() => {
      const map = props.map;
      if (!map || !styleReady(map)) {return;}

      const enabled = props.enabled;
      const allMarkers = alarmsExploreMapMarkers();

      // Only boats drawn on the map get markers.
      const markers = filterMarkersToSelectedSources(allMarkers, props.selectedSources);

      if (!enabled || !markers.length) {
        resolved.clear();
        // Empty the layer rather than removing it: the layer's lifetime belongs to the map effect,
        // and dropping it here would make every toggle a rebuild.
        if (ensureLayer(map)) {setData(map, []);}
        return;
      }

      const { getTimestamp, getLat, getLng } = makeLatLngAccessors();
      const track = (props.trackData ?? []) as unknown[];

      // The window the track covers IS the window the markers may occupy. Outside it there is
      // nothing to pin a marker to, and keeping one would leave it frozen at a position the boat
      // left long ago — this is the "fades out of the timeline" boundary.
      const window = computeTrackWindow(track, (row) => getTimestamp(row).getTime());

      const liveIds = new Set(markers.map((m) => m.id));
      for (const id of selectExpiredMarkerIds(resolved, liveIds, window)) resolved.delete(id);

      // Anything still unresolved and inside the window needs the track once.
      const pending = selectPendingMarkers(markers, new Set(resolved.keys()), window);

      if (pending.length > 0 && track.length > 0) {
        const interpolateOpts = { getTimestamp, getLat, getLng };
        const markersHaveSource = pending.some((m) => m.source !== '');
        const trackHasSource = markersHaveSource && track.some((d) => trackRowSourceKey(d) !== '');
        const sortedTrack = trackHasSource
          ? []
          : [...track].sort((a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime());

        // getInterpolatedPointAtTimePerSource walks the whole track per call, so group by instant
        // first: a day of alarms at one timestamp each would otherwise be O(markers x track).
        const byIso = new Map<string, AlarmExploreMapMarker[]>();
        for (const m of pending) {
          const list = byIso.get(m.atIso);
          if (list) {list.push(m);} else {byIso.set(m.atIso, [m]);}
        }

        for (const [atIso, group] of byIso) {
          const t = new Date(atIso);
          const atMs = t.getTime();
          if (Number.isNaN(atMs)) {continue;}

          const pointFor = (() => {
            if (!trackHasSource) {
              const p = getInterpolatedPointAtTimeFromSortedTrack(sortedTrack, t, interpolateOpts);
              return () => p;
            }
            const perSource = getInterpolatedPointAtTimePerSource(
              track,
              t,
              trackRowSourceKey,
              interpolateOpts
            );
            return (m: AlarmExploreMapMarker) => perSource.get(m.source.trim().toUpperCase());
          })();

          for (const m of group) {
            const p = pointFor(m);
            if (!p) {continue;}
            const lat = getLat(p);
            const lng = getLng(p);
            if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
              continue;
            }
            resolved.set(m.id, {
              lng,
              lat,
              atMs,
              category: m.category,
              tooltip: m.tooltip,
            });
          }
        }
      }

      if (!ensureLayer(map)) {return;}

      // The layer outlives a theme change, so repaint rather than rebuild.
      try {
        map.setPaintProperty?.(LYR_ID, 'circle-stroke-color', strokeColor());
      } catch {
        /* ignore */
      }

      const features = [];
      for (const [id, r] of resolved) {
        features.push({
          type: 'Feature',
          id,
          geometry: { type: 'Point', coordinates: [r.lng, r.lat] },
          properties: { category: r.category, tooltip: r.tooltip },
        });
      }
      setData(map, features);
    });
  };

  /**
   * LISTENERS AND LAYER LIFETIME — keyed on the map alone.
   *
   * This deliberately reads nothing but `props.map`. An `onCleanup` registered inside a
   * `createEffect` runs before EVERY re-run, not just on unmount, so when this teardown lived in
   * the data effect below it fired on each track tick (~1 Hz on a live map): the GL layer was
   * removed and re-added every second, which is what made the markers flash, and `resolved` was
   * cleared every second, which quietly defeated the whole resolve-once cache and put the
   * per-frame interpolation back. Keeping the two effects apart is what makes "draw once" true.
   */
  createEffect(() => {
    const map = props.map;
    if (!map) {return;}

    /**
     * Native tooltip, replacing the `<title>` the SVG circles carried.
     *
     * Layer-scoped listeners so Mapbox only hit-tests this one layer. The canvas `title` gives the
     * same OS-rendered hover tooltip the `<title>` element did, with no popup machinery.
     */
    const onEnter = (ev: { features?: Array<{ properties?: Record<string, unknown> }> }) => {
      try {
        const canvas = map.getCanvas?.();
        const tip = ev?.features?.[0]?.properties?.tooltip;
        if (canvas && typeof tip === 'string') {canvas.title = tip;}
      } catch {
        /* ignore */
      }
    };
    const onLeave = () => {
      try {
        const canvas = map.getCanvas?.();
        if (canvas) {canvas.title = '';}
      } catch {
        /* ignore */
      }
    };

    // The style may not be ready on the first run, and a style reload drops added layers; this is
    // how the layer is (re)built in both cases.
    const onStyleData = () => sync();

    try {
      map.on('mousemove', LYR_ID, onEnter);
      map.on('mouseleave', LYR_ID, onLeave);
      map.on('styledata', onStyleData);
    } catch {
      /* a map stub without layer-scoped events is fine; tooltips simply do not attach */
    }

    onCleanup(() => {
      try {
        map.off('mousemove', LYR_ID, onEnter);
        map.off('mouseleave', LYR_ID, onLeave);
        map.off('styledata', onStyleData);
      } catch {
        /* ignore */
      }
      onLeave();
      removeLayer(map);
      resolved.clear();
    });
  });

  /**
   * DATA — re-runs freely, and must therefore tear nothing down.
   *
   * Everything here is idempotent: `ensureLayer` is a no-op once the layer exists, and `setData`
   * on unchanged features is a no-op to the eye. Re-running this is cheap precisely because the
   * cache above survives it.
   */
  createEffect(() => {
    // Re-resolve when the channel names change: they decide which columns carry lat/lng.
    void defaultChannelsStore.latName();
    void defaultChannelsStore.lngName();
    void themeStore.isDark();
    void props.map;
    void props.enabled;
    void alarmsExploreMapMarkers();
    // Deselecting a boat must drop its markers, so this has to be a tracked read.
    void props.selectedSources;
    void props.trackData?.length;

    sync();
  });

  return null;
}
